import { Suspense, lazy } from 'react'
import { rectSortingStrategy } from '@dnd-kit/sortable'
import { GripVertical } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DashboardDataSource, Widget } from '@/api/types'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { byDecimalOrder } from '@/shared/lib/order'
import { isConfigured } from '../configuration'
import { useOrderWidgets } from '../mutations'
import { SummaryWidget } from './summary-widget'
import { WidgetFrame } from './widget-frame'
import { WidgetMenu } from './widget-menu'

/**
 * Loaded on demand, for the reason the formula editor is: the charting library
 * is 124 kB gzipped, more than a third of everything else put together, and a
 * session that never opens a dashboard has no use for any of it. A summary
 * widget draws no chart and stays in the main bundle.
 */
const ChartWidget = lazy(async () => {
  const module = await import('./chart-widget')
  return { default: module.ChartWidget }
})

/**
 * Everything on a dashboard, in the order it was arranged in, rearranged by
 * dragging a card's handle.
 *
 * One column on a narrow screen and two on a wide one. Upstream lays widgets out
 * in a single column at every width, which leaves a summary widget -- one number
 * -- occupying a full-width card.
 */
export function WidgetBoard({
  widgets,
  dashboardId,
  dataSources,
  onConfigure,
}: {
  widgets: readonly Widget[]
  dashboardId: number
  dataSources: readonly DashboardDataSource[]
  onConfigure: (widgetId: number) => void
}) {
  const byId = new Map(dataSources.map((source) => [source.id, source]))
  const ordered = byDecimalOrder(widgets)
  const order = useOrderWidgets(dashboardId)

  return (
    <SortableList
      ids={ordered.map((widget) => widget.id)}
      strategy={rectSortingStrategy}
      onReorder={(widgetIds) => {
        order.mutate(widgetIds)
      }}
    >
      <div className="grid gap-4 p-4 md:grid-cols-2">
        {ordered.map((widget) => (
          <SortableWidget key={widget.id} widgetId={widget.id} title={widget.title}>
            {(handle) => (
              <WidgetCard
                widget={widget}
                dashboardId={dashboardId}
                onConfigure={onConfigure}
                handle={handle}
                source={
                  widget.data_source_id === undefined
                    ? undefined
                    : byId.get(widget.data_source_id)
                }
              />
            )}
          </SortableWidget>
        ))}
      </div>
    </SortableList>
  )
}

/** One grid cell that can be dragged by the handle it gives its card. */
function SortableWidget({
  widgetId,
  title,
  children,
}: {
  widgetId: number
  title: string
  children: (handle: React.ReactNode) => React.ReactNode
}) {
  const { t } = useTranslation()
  const sortable = useSortableItem(widgetId)

  const handle = (
    <Button
      variant="ghost"
      size="icon"
      className="size-7 shrink-0 cursor-grab text-muted-foreground"
      aria-label={t('widget.reorder', { title })}
      {...sortable.handleProps}
    >
      <GripVertical />
    </Button>
  )

  return (
    <div
      ref={sortable.setNodeRef}
      style={sortable.style}
      className="relative grid data-[dragging=true]:z-10 data-[dragging=true]:opacity-80"
      data-dragging={sortable.isDragging}
    >
      {children(handle)}
    </div>
  )
}

/**
 * One widget, or the reason it cannot be drawn.
 *
 * A widget always has a data source -- creating one creates it, deleting one
 * deletes it -- so a widget whose source is not in the list is a widget whose
 * source has been trashed. It keeps its card and says so, because a card that
 * disappeared would leave nothing to fix.
 */
function WidgetCard({
  widget,
  dashboardId,
  source,
  onConfigure,
  handle,
}: {
  widget: Widget
  dashboardId: number
  source: DashboardDataSource | undefined
  onConfigure: (widgetId: number) => void
  handle: React.ReactNode
}) {
  const { t } = useTranslation()

  const actions = (
    <>
      {handle}
      <WidgetMenu
        dashboardId={dashboardId}
        widgetId={widget.id}
        title={widget.title}
        onConfigure={() => {
          onConfigure(widget.id)
        }}
      />
    </>
  )

  if (source === undefined) {
    return (
      <WidgetFrame
        widgetId={widget.id}
        title={widget.title}
        description={widget.description}
        misconfigured
        actions={actions}
      >
        <p className="text-muted-foreground">{t('widget.noDataSource')}</p>
      </WidgetFrame>
    )
  }

  // A widget nobody has finished making. Not an error and not badged as one:
  // the server refuses to dispatch a data source with no column chosen, so
  // asking would spend a request on a 400 and put a red badge on a card
  // somebody is in the middle of configuring.
  if (!isConfigured(source)) {
    return (
      <WidgetFrame
        widgetId={widget.id}
        title={widget.title}
        description={widget.description}
        actions={actions}
      >
        <p className="text-muted-foreground">{t('widget.unconfigured')}</p>
      </WidgetFrame>
    )
  }

  if (widget.type === 'lithos_chart') {
    return (
      <Suspense fallback={<ChartPlaceholder title={widget.title} actions={actions} />}>
        <ChartWidget
          widget={widget}
          dashboardId={dashboardId}
          source={source}
          actions={actions}
        />
      </Suspense>
    )
  }

  return (
    <SummaryWidget
      widget={widget}
      dashboardId={dashboardId}
      source={source}
      actions={actions}
    />
  )
}

/** The card a chart will fill, held open while the charting library arrives. */
function ChartPlaceholder({
  title,
  actions,
}: {
  title: string
  actions: React.ReactNode
}) {
  return (
    <WidgetFrame widgetId={0} title={title} actions={actions} loading>
      <Skeleton className="h-48 w-full" />
    </WidgetFrame>
  )
}
